import AppsIcon from '@mui/icons-material/Apps';
import { IconButton, Link, List, ListItem, ListItemText, ListSubheader } from '@mui/material';
import Menu from '@mui/material/Menu';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppURL } from '../../../utility/AppURL';

interface MenuItem {
    text: string;
    href: string;
    disabled?: boolean;
}

interface MenuSection {
    title?: string;
    items: MenuItem[];
}

export const MainMenu = () => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const getMenuConfig = document.cookie.split('; ').find(row => row.startsWith('DOVERUNNER_MENU_CONFIG='))?.split('=')[1];
    const menuConfig = JSON.parse(decodeURIComponent(getMenuConfig || '{"activeBrandArray":[],"isPartyHeadUser":false,"partyAuthorize":[]}'));

    const menuItems: MenuSection[] = [
        {
            items: [{ text: 'doverunner_home', href: AppURL.doverunnerDashboard }],
        },
        {
            title: 'product',
            items: [
                { text: 'appsecurity', href: AppURL.appSecurity },
                { text: 'contentsecurity', href: AppURL.contentSecurity, disabled: !menuConfig.activeBrandArray.includes('PallyCon') },
            ],
        },
        {
            title: 'setting',
            items: [
                {
                    text: 'user_management',
                    href: AppURL.userManagement,
                    disabled: !menuConfig.isPartyHeadUser,
                },
                { 
                    text: 'billing_management', 
                    href: AppURL.billingDetails, 
                    disabled: !menuConfig.partyAuthorize?.some((code: string) => ['AB001', 'AB002', 'AB003'].includes(code))
                },
            ],
        },
    ];

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <div>
            <IconButton
                onClick={handleClick}
                aria-controls={open ? 'main-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                sx={{ color: '#797A7C', padding: '4px', '&:hover': { color: '#A397BF' } }}
            >
                <AppsIcon />
            </IconButton>
            <Menu
                id="main-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'main-menu-button',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        padding: 0,
                        borderRadius: '12px',
                        minWidth: '204px',
                    },
                }}
            >
                {menuItems.map((section, index) => (
                    <List
                        key={index}
                        component="nav"
                        aria-labelledby={`nested-list-subheader-${index}`}
                        disablePadding
                        subheader={
                            section.title && (
                                <ListSubheader className="text-xs font-bold text-dr-black-200 mt-2" component="div" id={`nested-list-subheader-${index}`}>
                                    {t(section.title)}
                                </ListSubheader>
                            )
                        }
                    >
                        {section.items.map((item, itemIndex) => (
                            <ListItem
                                key={itemIndex}
                                component={Link}
                                href={item.href}
                                disabled={item.disabled}
                                onClick={handleClose}
                                sx={{
                                    padding: '4px 16px',
                                    color: 'inherit',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                <ListItemText primary={t(item.text)} />
                            </ListItem>
                        ))}
                    </List>
                ))}
            </Menu>
        </div>
    );
};
